import React from "react";
import { motion } from "motion/react";
import { Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Home, 
  Bed, 
  Waves, 
  Mountain, 
  Trees, 
  Shield,
  MapPin,
  Phone,
  CheckCircle,
  Play,
  Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { casaPremium, WHATSAPP_LINK, WHATSAPP_NUMBER, EMAIL } from "../data/mock";

const iconMap = {
  Home: Home,
  Bed: Bed,
  Waves: Waves,
  Mountain: Mountain,
  Trees: Trees,
  Shield: Shield,
};

const CasaPremiumPage = () => {
  const navigate = useNavigate();

  const getWhatsAppUrl = () => {
    const message = `Hello! I'm interested in renting the Itanhangá Luxury Villa. I'd like more information about availability and pricing.`;
    return `${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] lg:h-[70vh] overflow-hidden">
        <img
          src={casaPremium.images[0]}
          alt={casaPremium.nameEn}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate("/")}
          className="absolute top-24 left-6 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-gray-800 hover:bg-white transition-colors shadow-lg"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Home</span>
        </motion.button>

        {/* Video Button */}
        <a
          href={casaPremium.videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-24 right-6 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-gray-800 hover:bg-white transition-colors shadow-lg"
        >
          <Play className="w-4 h-4 text-[hsl(var(--coral))] fill-current" />
          <span className="text-sm font-medium">Watch Video Tour</span>
        </a>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="inline-block bg-[hsl(var(--golden))] text-gray-900 text-sm font-semibold px-4 py-1 rounded-full mb-4">
                Exclusive Property
              </span>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
                {casaPremium.nameEn}
              </h1>
              <div className="flex items-center gap-2 text-white/90">
                <MapPin className="w-5 h-5" />
                <span>{casaPremium.location}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Details */}
            <div className="lg:col-span-2">
              {/* Tagline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[hsl(var(--coral))] text-xl font-medium mb-6"
              >
                {casaPremium.tagline}
              </motion.p>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="prose prose-lg max-w-none mb-10"
              >
                <p className="text-gray-600 leading-relaxed text-lg">
                  {casaPremium.description}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  This magnificent property combines Swiss architectural excellence with the natural 
                  beauty of Rio de Janeiro's lush mountains. Every detail has been carefully designed 
                  to provide an unforgettable luxury experience for discerning travelers and those 
                  seeking the ultimate private retreat in Brazil.
                </p>
              </motion.div>

              {/* Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-10"
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                  Property Features
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {casaPremium.features.map((feature, index) => {
                    const Icon = iconMap[feature.icon];
                    return (
                      <div
                        key={index}
                        className="flex items-center gap-3 bg-[hsl(var(--sand))] p-4 rounded-xl"
                      >
                        <div className="w-10 h-10 rounded-full bg-[hsl(var(--golden))/0.2] flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[hsl(var(--golden))]" />
                        </div>
                        <span className="text-gray-800 font-medium">
                          {feature.textEn}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-10"
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                  Why Choose This Property
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {casaPremium.highlightsEn.map((highlight, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[hsl(var(--whatsapp))] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{highlight}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Video Embed */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-10"
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                  Video Tour
                </h2>
                <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                  <iframe
                    src={casaPremium.videoEmbed}
                    title="Itanhangá Luxury Villa Video Tour"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </motion.div>

              {/* Image Gallery */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">
                  Gallery
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {casaPremium.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Itanhangá Luxury Villa - View ${index + 1}`}
                      className="w-full h-64 object-cover rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                    />
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="sticky top-24 bg-white rounded-2xl shadow-xl border border-gray-100 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[hsl(var(--golden))] text-[hsl(var(--golden))]" />
                  ))}
                  <span className="text-gray-600 text-sm ml-2">Premium Property</span>
                </div>

                <h3 className="font-display text-xl font-bold text-gray-900 mb-2">
                  Book Your Stay
                </h3>
                <p className="text-gray-600 text-sm mb-6">
                  Contact us directly for availability and personalized pricing for your dates.
                </p>

                <div className="space-y-4 mb-6">
                  <motion.a
                    href={getWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="block"
                  >
                    <Button className="w-full bg-[hsl(var(--whatsapp))] hover:bg-[hsl(142_69%_38%)] text-white rounded-full py-4 h-auto font-semibold gap-2">
                      <Phone className="w-5 h-5" />
                      WhatsApp: {WHATSAPP_NUMBER}
                    </Button>
                  </motion.a>

                  <a href={`mailto:${EMAIL}`} className="block">
                    <Button
                      variant="outline"
                      className="w-full rounded-full py-4 h-auto font-medium"
                    >
                      Email Us
                    </Button>
                  </a>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Location</h4>
                  <p className="text-gray-600 text-sm mb-4">
                    {casaPremium.location}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Itanhangá is one of Rio's most prestigious and private neighborhoods, 
                    surrounded by the Tijuca Forest and offering stunning mountain views.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 bg-[hsl(var(--sand))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/">
            <Button
              variant="outline"
              className="rounded-full px-8 py-3 h-auto font-medium gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to All Services
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CasaPremiumPage;
